<template>
  <section class="core-dna-module-component">
    <div class="inner-container">
      <div class="columns">
        <div
          class="line"
          :style="`background-color: ${data.color};`"></div>
        <div
          class="orb"
          :style="`background-color: ${data.color}; box-shadow: 0 0 20px ${data.color};`"></div>
        <div class="column one">
          <div
            class="title-area"
            v-html="data.title"></div>
          <template
            v-for="(value, indx) of data.values"
            :key="`value-card-${indx}`">
            <transition name="fade">
              <div
                v-if="indx === activeDNAItem"
                class="pop-up-card"
                :style="`background-color: ${colors[indx]};`"
                :class="`${indx === 0 || indx === 3 ? 'white-text' : 'black-text'}`">
                <div class="inner-container">
                  <div class="title-section">
                    <div class="title">{{ value.title }}</div>
                    <div
                      class="close"
                      @click="setActiveDNAItem(4)">
                      <Gems :index="indx" />
                      <svg
                        class="cross"
                        width="36"
                        height="37"
                        viewBox="0 0 36 37"
                        fill="none">
                        <line
                          x1="26.5899"
                          y1="9.45808"
                          x2="8.97189"
                          y2="27.0761"
                          stroke="white"
                          stroke-width="2" />
                        <path
                          d="M9.14453 9.63135L26.7626 27.2494"
                          stroke="white"
                          stroke-width="2" />
                      </svg>
                    </div>
                  </div>
                  <div class="content">
                    <div
                      class="text"
                      v-html="value.description"></div>
                  </div>
                </div>
                <div class="bottom-image">
                  <img
                    :src="value.image.url"
                    :alt="value.image.alt" />
                </div>
              </div>
            </transition>
          </template>
        </div>
        <div class="column">
          <DNA
            :purple="data.values[0].title"
            :cyan="data.values[1].title"
            :lilac="data.values[2].title"
            :orange="data.values[3].title" />
        </div>
      </div>
    </div>
    <div class="bg-container">
      <div class="bg-video">
        <div class="overlay"></div>
        <video
          v-if="data.video_background"
          playsinline
          autoplay
          loop
          muted>
          <source
            :src="data.video_background.url"
            type="video/mp4" />
        </video>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "CoreDNAComponent",
  "CLASSNAME": "core-dn-a-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */
import DNA from '../utility/DNA.vue'
import Gems from '../utility/Gems.vue'
import useActiveDNAItem from '@/composables/useActiveDNAItem'

const { activeDNAItem, setActiveDNAItem } = useActiveDNAItem()
defineProps(['data'])
// console.log('CoreDNAComponent', props.data)
const colors = ['#382C75', '#6FCFD7', '#B0C5FF', '#FF8931']
</script>

<style lang="scss">
.core-dna-module-component {
  @apply relative h-[80vh] xl:h-[100vh];
  .inner-container {
    @apply relative z-10 h-full;
    .columns {
      @apply grid grid-cols-1 lg:grid-cols-2 gap-2 h-full;
      .line {
        @apply absolute left-[30px] lg:left-[60px] xl:left-[75px] top-0 z-10 h-full w-[1px];
      }
      .orb {
        @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full left-[calc(30px-12px)] lg:left-[calc(60px-16px)] xl:left-[calc(75px-16px)] blur-[2px] top-14;
      }
      .column {
        @apply relative z-10 px-2 md:px-10 lg:px-0 flex flex-col h-[80vh] md:h-[70vh] lg:h-screen;
        &.one {
          @apply z-20 h-fit lg:h-full;
        }
        .DNA-values {
          @apply h-auto;
        }
        .title-area {
          @apply pl-6 lg:pl-12 pt-12;
          * {
            @apply heading-3;
            strong {
              @apply text-lm_purple;
            }
          }
        }
        .pop-up-card {
          @apply absolute mt-24 md:mt-32 -left-0 right-0 mx-auto lg:-left-28 w-full max-w-[545px] z-2;
          .inner-container {
            @apply p-4 md:p-8;
            .title-section {
              @apply flex justify-between pb-4 border-b border-white;
              .title {
                @apply text-black font-nuances text-[30px] xl:text-[36px] font-normal leading-[33px] xl:leading-[50px] tracking-[-0.44px];
              }
              .close {
                @apply flex items-center justify-between relative w-8 h-8 xl:w-12 xl:h-12;
                .cross {
                  @apply absolute m-auto left-0 right-0 cursor-pointer w-8 h-8 xl:w-12 xl:h-12;
                }
              }
            }
            .content {
              @apply pt-4 max-h-[35vh] overflow-y-auto;
              .text * {
                @apply text-black small xl:text-[22px] leading-tight;
              }
            }
          }
          .bottom-image {
            @apply w-full max-h-[30vh] md:max-h-[370px] xl:max-h-[36vh] overflow-hidden;
            img {
              @apply object-cover w-full h-full;
            }
          }
          &.white-text {
            .inner-container {
              .title-section {
                .title {
                  @apply text-white;
                }
              }
              .content {
                .text * {
                  @apply text-white;
                }
              }
            }
          }
        }
      }
    }
  }
  .bg-container {
    @apply absolute bottom-0 left-0;
    .bg-video {
      @apply relative max-w-[90vw] max-h-[930px];
      .overlay {
        @apply absolute inset-0 z-2;
        background: linear-gradient(
            90deg,
            rgba(243, 240, 237, 0) 9.68%,
            #fff 65.59%
          ),
          linear-gradient(0deg, #382c75 0%, #382c75 100%),
          linear-gradient(0deg, #000 0%, #000 100%);
        mix-blend-mode: screen;
      }
      video {
        @apply saturate-[0.3] z-1 opacity-80;
      }
    }
  }
}
</style>
